import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { string } from 'prop-types';
import { Onboarding } from '@templates';
import { Typography, Button, Flex } from '@mixins';
import { theme } from '@styles';
import { routes } from '@constants';
import { LOADING } from '@constants/requestPhase';
import { ONBOARDING_STEPS, ONBOARDING_STEPS_LABELING } from '@constants/common';
import { trackEvent } from '@utils/mixpanel';
import { submitCompanyOnboarding } from '@store/company/duck';

import CompleteOnboardingImage from '@assets/images/CompleteOnboarding.png';
import { ReactComponent as Checkmark } from '@assets/svgs/Checkmark.svg';
import { Icon } from '@atoms';

const CompleteOnboarding = ({ direction }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { currentLocation } = useSelector(store => store.locationStore);
  const { onboardingState, submitCompanyOnboardingPhase } = useSelector(store => store.companyStore);

  const [isLoading, setIsLoading] = useState(false);

  const isProofOfPaymentStepAvailable = useMemo(
    () => onboardingState?.availableSteps?.includes(ONBOARDING_STEPS.PROOF_OF_PAYMENT),
    [],
  );

  const handleBack = () => {
    history.push(isProofOfPaymentStepAvailable ? routes.proofOfPaymentOnboarding : routes.companyProfileOnboarding, {
      direction: 'back',
    });
  };

  const handleSaveClick = () => {
    trackEvent('Onboarding action', {
      Title: 'Save & Continue Later',
    });

    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      history.push(routes.home);
    }, 500);
  };

  const handleCompleteOnboarding = () => {
    trackEvent('Onboarding action', {
      Title: 'Done',
    });

    dispatch(submitCompanyOnboarding());
  };

  const checkIfStepCompleted = useCallback(
    step => {
      switch (step) {
        case ONBOARDING_STEPS.PROOF_OF_PAYMENT:
          return !!onboardingState?.proofOfPaymentFileUrl;
        case ONBOARDING_STEPS.COMPANY_LOGO_ON_DOOR:
          return !!onboardingState?.logoOnDoorFileUrl;
        case ONBOARDING_STEPS.CUSTOM_REQUESTS:
          return !!onboardingState?.customRequests?.length;
        case ONBOARDING_STEPS.SCHEDULE_ORIENTATION:
          return !!onboardingState?.orientationDate;
        default:
          return true;
      }
    },
    [onboardingState],
  );

  const handleEmailClick = () => {
    window.location.href = `mailto:${currentLocation?.cmTeamEmail ?? 'cm-team@mindspace.me'}`;
  };

  const availableOnboardingSteps = useMemo(() => {
    const stepsOrder = Object.values(ONBOARDING_STEPS);
    return (onboardingState?.availableSteps || [])
      .filter(step => step !== ONBOARDING_STEPS.WELCOME)
      .sort((a, b) => stepsOrder.indexOf(a) - stepsOrder.indexOf(b));
  }, [onboardingState?.availableSteps]);

  const splitIndex = Math.ceil(availableOnboardingSteps.length / 2);
  const firstColumnSteps = availableOnboardingSteps.slice(0, splitIndex);
  const secondColumnSteps = availableOnboardingSteps.slice(splitIndex);

  const onboardingSteps = useMemo(
    () => (
      <Flex display="grid" gridTemplateColumns="1fr 1fr" columnGap={[20, 40]} mb={10}>
        <Flex flexDirection="column">
          {firstColumnSteps.map(step => {
            const isStepCompleted = onboardingState?.finishedSteps?.includes(step) && checkIfStepCompleted(step);
            return (
              <Flex key={step} mb={[12, 16]} alignItems="center">
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  mr="10px"
                  width="20px"
                  height="20px"
                  border={isStepCompleted ? 'none' : `1px solid ${theme.color.mintMinded[300]}`}
                  backgroundColor={isStepCompleted ? theme.color.mintMinded[300] : 'transparent'}
                  borderRadius="50%">
                  {isStepCompleted && <Icon SVG={Checkmark} width={13} height={9} />}
                </Flex>
                <Typography variant="diatype-300" fontSize={[12, 14, 16, 18]} color={theme.color.mintMinded[900]}>
                  {ONBOARDING_STEPS_LABELING[step]}
                </Typography>
              </Flex>
            );
          })}
        </Flex>

        <Flex flexDirection="column">
          {secondColumnSteps.map(step => {
            const isStepCompleted = onboardingState?.finishedSteps?.includes(step) && checkIfStepCompleted(step);
            return (
              <Flex key={step} mb={[12, 16]} alignItems="center">
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  mr="10px"
                  width="20px"
                  height="20px"
                  border={isStepCompleted ? 'none' : `1px solid ${theme.color.mintMinded[300]}`}
                  backgroundColor={isStepCompleted ? theme.color.mintMinded[300] : 'transparent'}
                  borderRadius="50%">
                  {isStepCompleted && <Icon SVG={Checkmark} width={13} height={9} />}
                </Flex>
                <Typography variant="diatype-300" fontSize={[12, 14, 16, 18]} color={theme.color.mintMinded[900]}>
                  {ONBOARDING_STEPS_LABELING[step]}
                </Typography>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    ),
    [firstColumnSteps, secondColumnSteps, onboardingState, checkIfStepCompleted],
  );

  return (
    <Onboarding
      showBackButton
      direction={direction}
      title="You're almost ready for your move-in!"
      image={CompleteOnboardingImage}
      showNavigationItems={false}
      onBack={handleBack}>
      <Flex flexDirection="column">
        <Typography
          whiteSpace="pre-line"
          variant="diatype-300"
          fontSize={[14, 16, 18, 20]}
          mb={[20, 24]}
          color={theme.color.mintMinded[900]}>
          {`We’re here to make your move as smooth as possible. 
        Have questions? `}
          <Typography
            pointer
            variant="diatype-300"
            color={theme.color.mintMinded[300]}
            display="inline"
            textDecoration="underline"
            onClick={handleEmailClick}>
            Contact us
          </Typography>
          {` anytime: ${currentLocation?.cmTeamEmail}`}
        </Typography>
        {onboardingSteps}
        <Flex mb="10px">
          <Button
            onClick={handleCompleteOnboarding}
            mr={['8px', 20, 20, 20]}
            variant="primary"
            py={10}
            px={20}
            disabled={isLoading}
            minWidth={[160, 213, 238, 265]}
            fontSize={[11, 14, 16, 18]}>
            {submitCompanyOnboardingPhase === LOADING ? 'Saving...' : 'Done, provided all that I can'}
          </Button>
          <Button
            onClick={handleSaveClick}
            py={10}
            px={20}
            disabled={submitCompanyOnboardingPhase === LOADING}
            alignSelf="end"
            variant="secondary"
            minWidth={[140, 181, 201, 221]}
            fontSize={[11, 14, 16, 18]}>
            {isLoading ? 'Saving...' : 'Save & Continue Later'}
          </Button>
        </Flex>
        <Typography fontSize={[10, 12, 14]} color={theme.color.mintMinded[900]}>
          You can always come back and update, up to a week before move-in
        </Typography>
      </Flex>
    </Onboarding>
  );
};

CompleteOnboarding.defaultProps = {
  direction: 'forward',
};

CompleteOnboarding.propTypes = {
  direction: string,
};

export default CompleteOnboarding;
